import { DATA, type ItemDef, type RecipeDef, type ShopItem } from '../types.js';
import type { StatBonuses } from './attributes.js';

/**
 * feature 2.3 — the shop (buy raw stats & attributes).
 *
 * A fixed, ordered catalog (`DATA.shop`) of permanent, run-long stat upgrades a
 * player buys with their own gold during the wave break. Server-authoritative,
 * deterministic (no RNG, no rotating stock). The cost / bonus maths lives here
 * so the client HUD, the server room and the tests all agree.
 */

/** The n-th purchase (0-indexed — how many the player already owns) of `item`
 * costs `baseCost + n * costStep` (criterion 4). */
export function shopCost(item: ShopItem, boughtSoFar: number): number {
  return item.baseCost + boughtSoFar * item.costStep;
}

/** Resolve a catalog entry by id, or `undefined` for an unknown id. */
export function shopItem(id: string): ShopItem | undefined {
  return DATA.shop.find((i) => i.id === id);
}

/** The five stat deltas a set of shop purchases can produce (feature 2.3). */
export type ShopBonuses = Required<
  Pick<StatBonuses, 'strength' | 'agility' | 'energy' | 'bonusHp' | 'bonusSpeed'>
>;

/** A `StatBonuses` with every field present (`itemBonuses` / `mergeBonuses`). */
export type FullStatBonuses = Required<StatBonuses>;

function zeroBonuses(): FullStatBonuses {
  return {
    strength: 0,
    agility: 0,
    energy: 0,
    bonusHp: 0,
    bonusSpeed: 0,
    bonusArmor: 0,
    bonusDamage: 0,
    bonusAttackSpeedPct: 0,
  };
}

/**
 * Turn a player's per-item purchase counts into the stat bonuses
 * `effectiveStats` / `World.recomputeStats` consume: attribute items
 * (`strength` / `agility` / `energy`) contribute `count * amountPerBuy` to that
 * attribute; the `maxHp` item → `bonusHp`; the `moveSpeed` item → `bonusSpeed`.
 * Catalog iterated in array order (deterministic).
 */
export function shopBonuses(buys: Record<string, number>): ShopBonuses {
  const b: ShopBonuses = { strength: 0, agility: 0, energy: 0, bonusHp: 0, bonusSpeed: 0 };
  for (const item of DATA.shop) {
    const n = buys[item.id] ?? 0;
    if (n <= 0) continue;
    const total = n * item.amountPerBuy;
    switch (item.stat) {
      case 'strength':
        b.strength += total;
        break;
      case 'agility':
        b.agility += total;
        break;
      case 'energy':
        b.energy += total;
        break;
      case 'maxHp':
        b.bonusHp += total;
        break;
      case 'moveSpeed':
        b.bonusSpeed += total;
        break;
    }
  }
  return b;
}

/**
 * feature 3.1 — resolve an item catalog entry by id, or `undefined` for an
 * unknown id.
 */
export function itemDef(id: string): ItemDef | undefined {
  return DATA.items[id];
}

/**
 * feature 3.1 — sum the `stats` of every held item into a `StatBonuses`.
 *
 * `strength` / `agility` / `energy` item stats feed the matching attribute (so
 * they scale through the attribute formulas exactly like a shop attribute buy);
 * `maxHp` → `bonusHp`, `moveSpeed` → `bonusSpeed`, `armor` → `bonusArmor`,
 * `damage` → `bonusDamage`, `attackSpeedPct` → `bonusAttackSpeedPct`.
 *
 * The inventory array is iterated in slot order and unknown / `null` slots are
 * skipped; catalog lookups are by id — deterministic, no RNG.
 */
export function itemBonuses(inventory: readonly (string | null)[]): FullStatBonuses {
  const b = zeroBonuses();
  for (const id of inventory) {
    if (!id) continue;
    const item = DATA.items[id];
    if (!item) continue;
    const s = item.stats;
    b.strength += s.strength ?? 0;
    b.agility += s.agility ?? 0;
    b.energy += s.energy ?? 0;
    b.bonusHp += s.maxHp ?? 0;
    b.bonusSpeed += s.moveSpeed ?? 0;
    b.bonusArmor += s.armor ?? 0;
    b.bonusDamage += s.damage ?? 0;
    b.bonusAttackSpeedPct += s.attackSpeedPct ?? 0;
  }
  return b;
}

/**
 * feature 3.3 — resolve a forge recipe by id, or `undefined` for an unknown id.
 * Iterates `DATA.recipes` in declaration order (deterministic).
 */
export function recipeDef(id: string): RecipeDef | undefined {
  return DATA.recipes.find((r) => r.id === id);
}

/**
 * feature 3.3 — multiset containment: does `inventory` currently hold every id
 * in `needed` with at least the required multiplicity? `needed` may contain
 * duplicates (`["whetstone", "whetstone"]` needs `whetstone` in two slots).
 * `null` / empty slots and ids not in `needed` are ignored. Pure and
 * order-independent — counts occurrences on both sides.
 */
export function inventoryContains(
  inventory: readonly (string | null)[],
  needed: readonly string[],
): boolean {
  const have = new Map<string, number>();
  for (const id of inventory) {
    if (!id) continue;
    have.set(id, (have.get(id) ?? 0) + 1);
  }
  const want = new Map<string, number>();
  for (const id of needed) want.set(id, (want.get(id) ?? 0) + 1);
  for (const [id, n] of want) {
    if ((have.get(id) ?? 0) < n) return false;
  }
  return true;
}

/**
 * feature 3.4 — weighted random pick. `weights` maps a key to a non-negative
 * relative weight; entries are walked in a **stable order — ascending by key**
 * (`Object.keys(weights).sort()`) so a given rng state always yields the same
 * key regardless of JSON / object key order. Draws `rng()` exactly once:
 * `r = rng() * totalWeight`, then subtracts each weight until `r < 0`. Falls
 * back to the last key if float error leaves `r` unconsumed. Returns `''` only
 * for an empty / all-zero-weight map.
 */
export function pickWeighted(rng: () => number, weights: Record<string, number>): string {
  const keys = Object.keys(weights).sort();
  let total = 0;
  for (const k of keys) total += Math.max(0, weights[k] ?? 0);
  if (total <= 0) return '';
  let r = rng() * total;
  for (const k of keys) {
    r -= Math.max(0, weights[k] ?? 0);
    if (r < 0) return k;
  }
  return keys[keys.length - 1]!;
}

/**
 * feature 3.1 — merge any number of partial `StatBonuses` (shop + items) into
 * one fully-populated object, summing every field. Parts are added in argument
 * order (deterministic).
 */
export function mergeBonuses(...parts: StatBonuses[]): FullStatBonuses {
  const b = zeroBonuses();
  for (const p of parts) {
    b.strength += p.strength ?? 0;
    b.agility += p.agility ?? 0;
    b.energy += p.energy ?? 0;
    b.bonusHp += p.bonusHp ?? 0;
    b.bonusSpeed += p.bonusSpeed ?? 0;
    b.bonusArmor += p.bonusArmor ?? 0;
    b.bonusDamage += p.bonusDamage ?? 0;
    b.bonusAttackSpeedPct += p.bonusAttackSpeedPct ?? 0;
  }
  return b;
}
